/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./SlotsAPISpec',
	'../SlotsAPI',
	'./Slot',
	'./MappedDataItem',
	'./model/SlotMappingModel'
], (_, APIFactory, SlotsAPISpec, SlotsAPI, Slot, MappedDataItem, SlotMappingModel) => {
	class SlotsImpl extends SlotsAPISpec{

		constructor(widgetModel, dataModel, visualizationImpl, transaction, locale, filterSupport) {
			super();
			this.widgetModel = widgetModel;
			this.visualizationImpl = visualizationImpl;
			this.transaction = transaction;
			this.locale = locale;
			this.filterSupport = filterSupport;

			this._dataItemImplMap = {};
			this.dataModel = dataModel;
			if (!this.widgetModel.slotmapping) {
				this.widgetModel.slotmapping = {};
			}
			this.slotMappingModel = new SlotMappingModel(this.widgetModel.slotmapping);

			this._slots = {};
			this._slotAPIList = null;
			this.setDefinition(this.visualizationImpl.getDefinition());

			this._dataItemImplMap = null;
			this._initializeDataItems();

			this._api = APIFactory.createAPI(this, [SlotsAPI]);
		}

		destroy(){
			this.widgetModel = null;
			this.visualizationImpl = null;
			this.transaction = null;
			this.locale = null;
			this.filterSupport = null;

			this.dataModel = null;
			this.slotMappingModel = null;

			for(let id in this._slots){
				this._slots[id].destroy();
			}
			this._slots = null;

			this._slotAPIList = null;

			for(let id in this._dataItemImplMap){
				this._dataItemImplMap[id].destroy();
			}
			this._dataItemImplMap = null;

			this._api = null;
		}

		getLocalFilters() {
			return this.filterSupport.getLocalFilters();
		}

		_initializeDataItems() {
			this._dataItemImplMap = {};
			this.dataModel.getDataItemList().forEach(dataItemModel => {
				this._createDataItemImpl(dataItemModel);
			});
		}

		_createDataItemImpl(model){
			const slotModel = this.slotMappingModel.getMappedSlot(model.id);
			const slot = slotModel ? this.getSlot(slotModel.name) : null;

			const impl =  new MappedDataItem(
				this.filterSupport,
				model,
				slot,
				this.getDataSource(),
				this.transaction,
				this.locale,
				this.visualizationImpl,
				this
			);
			APIFactory.setParentChildRelation(this, impl);
			this._dataItemImplMap[model.id] = impl;
			return impl;
		}

		setDefinition(definition){
			this.definition = definition;
			this._initializeSlots();
		}

		_initializeSlots() {
			const newSlotAPIList = [];
			const newSlotsMap = {};
			this.definition.getSlotList().forEach(slotDefinition => {
				let slot = this.getSlotImpl(slotDefinition.getId());
				if (slot){
					slot.setDefinition(slotDefinition);
				} else {
					slot = new Slot(
						slotDefinition,
						this.filterSupport,
						this,
						this.transaction,
						this.locale,
						this.visualizationImpl,
						this.dataModel,
						this.slotMappingModel
					);
					APIFactory.setParentChildRelation(this, slot);
				}
				newSlotsMap[slotDefinition.getId()] = slot;
				newSlotAPIList.push(slot.getAPI());
			});

			const oldSlots = this._slots;
			this._slots = newSlotsMap;
			this._slotAPIList = newSlotAPIList;
			Object.freeze(this._slotAPIList);

			// Go over the current list of data items.. and clear the slot for those that are not mapped to a valid slot
			for (let id in this._dataItemImplMap){
				const dataItem = this._dataItemImplMap[id];
				const slot = this._dataItemImplMap[id].getSlot();
				if (slot && !this._slots[slot.getId()]) {
					this.slotMappingModel.unmapDataItemsFromSlots([id]);
					dataItem.setSlot(null);
				}
			}

			// destoy old slot that are no longer valid after the new definition
			for (let id in oldSlots) {
				if (!this._slots[id]){
					oldSlots[id].destroy();
				}
			}
		}

		getDataSource() {
			return this.visualizationImpl.getDataSource();
		}

		createDataItems(dataItemSpecList) {
			const result = [];
			if (dataItemSpecList){
				const ids = [];
				dataItemSpecList.forEach(spec => {
					ids.push(spec.id);
					const model = this.dataModel.addDataItem(spec);
					if (!this._dataItemImplMap[spec.id]){
						this._createDataItemImpl(model);
					}
					result.push(this._dataItemImplMap[spec.id].getAPI());
				});
			}
			return result;
		}

		deleteDataItems(dataItemIdList, transaction) {
			const result = [];
			const hasLocalFilter = this.visualizationImpl.getLocalFilters().getFilterList().length > 0;
			if (dataItemIdList){
				dataItemIdList.forEach(id => {
					const dataItem = this._dataItemImplMap[id];
					if (dataItem){
						result.push({
							id: dataItem.getId(),
							columnId: dataItem.getColumnId()
						});
						dataItem.destroy();
						delete this._dataItemImplMap[id];
						this.slotMappingModel.unmapDataItemsFromSlots([id]);
						this.dataModel.removeDataItem(id, hasLocalFilter);
					}
				});
			}
			if (!hasLocalFilter && this.getDataItemList().length === 0) {
				// If viz is empty, remove selection filters added in this viz (e.g. added by navigation on a member)
				this.filterSupport.clearVizSelections(transaction);
			}
			return result;
		}

		getDataItemImpl(id){
			return this._dataItemImplMap[id] ? this._dataItemImplMap[id]: null;
		}

		getDataItemImplList(){
			return Object.values(this._dataItemImplMap);
		}

		getDataItem(id){
			const impl = this.getDataItemImpl(id);
			return impl ? impl.getAPI(): null;
		}

		getDataItemList(){
			// TODO - livewidget cleanup - cache and free the list for performance
			const result = [];
			for (let id in this._dataItemImplMap){
				result.push(this._dataItemImplMap[id].getAPI());
			}
			return result;
		}

		/**
		 * set DataItems@dataItemIds to given slot@slotId (and remove the dataItems from Original slot)
		 * @param {*} dataItemIds
		 * @param {*} slotId
		 * @param {*} options
		 *
		 * e.g.
		 * slotA: item1, item2
		 * slotB: item3
		 * if try move item2 from slotA to slotB,
		 *
		 * function will be called setDataItems([item2, item3], slotB)
		 */
		setDataItems(dataItemIds, slotId, transactionToken) {
			//step2: set dataItems to target slot
			// We use the implementation to access certain slot functionality that is not pubblic API
			// We also use the implementation to avoid firing individual slot events when using some slot functions 
			const targetSlot = this.getSlot(slotId);
			if (targetSlot){

				const subTransaction = this.transaction.startTransaction(transactionToken);
				//step1:unmap the data items if they are mapped to another slot
				_.each(dataItemIds, (dataItemId) => {
					const currentMapping = this.getMappingInfo(dataItemId);
					if (currentMapping && currentMapping.slot && currentMapping.slot.getId() !== slotId) {
						currentMapping.slot.removeDataItemsMapping([currentMapping.dataItem.getId()], subTransaction);
					}
				});

				const dataItemsTobeRemoved = [];
				const currentDataItemIds = [];
				targetSlot.getDataItemList().forEach(dataItem => {
					const id = dataItem.getId();
					currentDataItemIds.push(id);
					if (dataItemIds.indexOf(id) === -1){
						dataItemsTobeRemoved.push(dataItem.getId());
					}
				});

				// step2: clear target slot
				if (currentDataItemIds.length > 0){
					targetSlot.removeDataItemsMapping(currentDataItemIds, subTransaction);
				}
				// step3: add the new mappings	
				if (dataItemIds.length > 0){
					targetSlot.addDataItemsMapping(dataItemIds, null ,subTransaction);
				}
				// TODO livewidget_cleanup - the original implementation did not clean up unmapped items
				// we just need to decide the behavior of this API

				// step4: remove what used to be in the target slot and not longer mapped.
				//this.getAPI().deleteDataItems(dataItemsTobeRemoved, subTransaction);

				this.transaction.endTransaction(subTransaction);
			}
		}

		swapSlots(sourceSlotId, targetSlotId, transactionToken) {
			const subTransaction = this.transaction.startTransaction(transactionToken);

			const sourceSlot = this.getSlot(sourceSlotId);
			const targetSlot = this.getSlot(targetSlotId);

			const sourceSlotDataItems = sourceSlot.getDataItemList();
			sourceSlot.removeDataItemsMapping(sourceSlotDataItems.map(dataItem => dataItem.getId()), subTransaction);

			const targetSlotDataItems = targetSlot.getDataItemList();
			targetSlot.removeDataItemsMapping(targetSlotDataItems.map(dataItem => dataItem.getId()), subTransaction);

			sourceSlot.addDataItemsMapping(targetSlotDataItems.map(dataItem => dataItem.getId()), null, subTransaction);

			targetSlot.addDataItemsMapping(sourceSlotDataItems.map(dataItem => dataItem.getId()), null, subTransaction);

			this.transaction.endTransaction(subTransaction);

		}

		getSlot(slotId) {
			const slots = this.getSlotList();
			return _.find(slots, (slot) => {
				return slot.getId() === slotId;
			});
		}

		getSlotList() {
			// TODO - livewidget cleanup - cache and free the list for performance
			return this._slotAPIList;

		}

		getMappedSlotList() {
			// TODO - livewidget cleanup - cache and free the list for performance
			const slots = this.getSlotList();
			let slotsWithDataItems = [];
			for( let  i=0; i <slots.length; i++ ) {
				const dataItems = slots[i].getDataItemList() || [];
				if( dataItems.length > 0 ) {
					slotsWithDataItems.push( slots[i] );
				}
			}
			return slotsWithDataItems;
		}

		getMappingInfo(dataItemId) {
			const mappingInfoList = this.getMappingInfoList();
			return _.find(mappingInfoList, (mappingInfo) => {
				return mappingInfo.dataItem.getId() === dataItemId;
			});
		}

		getMappingInfoList() {
			// TODO - livewidget cleanup - cache and free the list for performance
			const result = [];
			this.definition.getSlotList().forEach(slotDefinition => {
				const slot = this.getSlot(slotDefinition.getId());
				const slotImpl = this.getSlotImpl(slotDefinition.getId());
				slotImpl.getDataItemList().forEach(dataItem => {
					result.push({
						slot: slot,
						dataItem: dataItem,
						indexInSlot: slot ? slotImpl.getDataItemIndex(dataItem.getId()) : -1
					});
				});
			});
			return result;
		}

		/**
		 * @implements SlotsAPI#isMappingComplete
		 */
		isMappingComplete(layerId) {
			const mappedSlotIds = this.getMappedSlotList().map(slot => slot.getDefinition().getId());
			const requiredSlots = this.getSlotList().filter(slot => !slot.getDefinition().isOptional());
			let datasetList = this.visualizationImpl.getDefinition().getDatasetList();
			if(layerId) {
				datasetList = [{ id: layerId }];
			}
			const sharingSlots = this.getSlotList().filter(slot => slot.getDefinition().getDatasetIdList().length > 1);
			// if the slot is sharing in two datasets, it's not multilayer
			const isMultilayers = datasetList.length > 1 && !sharingSlots.length > 0;
			const mappedDatasetList = datasetList.filter(dataset => {
				const unmappedRequiredSlots = requiredSlots.filter(slot => {
					const slotDatasetIdList = slot.getDefinition().getDatasetIdList();
					return slotDatasetIdList.includes(dataset.id) && !mappedSlotIds.includes(slot.getDefinition().getId());
				});
				return unmappedRequiredSlots.length === 0;
			});
			// If there is only one layer, all datasets should be mapped.
			if(!isMultilayers) {
				return datasetList.length === mappedDatasetList.length;
			}
			return mappedDatasetList.length > 0;
		}

		clearAllSlots(transactionToken) {
			this.getSlotList().forEach(slot => {
				slot.removeDataItems(slot.getDataItemList().map(dataItem => dataItem.getId()), transactionToken);
			});
		}

		getSlotImpl(slotId){
			return this._slots[slotId];
		}

		getAPI(){
			return this._api;
		}
	}

	return SlotsImpl;
});
